import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Clock, Mail, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const OutputGallery = () => {
  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Automated Setup",
      description: "Create a product brief and we handle the rest - domains, mailboxes, and campaigns"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "AI Finds Your Prospects",
      description: "Our system searches 250M+ contacts to find your ideal customers"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Personalized at Scale",
      description: "Every email is uniquely written for each prospect using AI"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Runs on Autopilot",
      description: "Set it once and let it run - no daily management needed"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-green-50 text-green-700 border-green-200">
            How It Works
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Everything Automated, Nothing to Manage
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Traditional tools make you do the work. We do it all for you.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
            >
              <div className="text-blue-600 mb-4">{feature.icon}</div>
              <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-sm">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* What to Expect */}
        <div className="mt-16 bg-white rounded-xl shadow-lg p-8 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            What You Can Realistically Expect
          </h3>
          <div className="space-y-4 text-gray-700">
            <p>
              ✓ <strong>Setup in 2 minutes:</strong> Just fill out a product brief
            </p>
            <p>
              ✓ <strong>Campaigns launch automatically:</strong> No manual work required
            </p>
            <p>
              ✓ <strong>Higher open rates:</strong> 25-45% with dedicated mailboxes (vs 10-15% industry average)
            </p>
            <p>
              ✓ <strong>AI-written emails:</strong> Each one personalized to the prospect
            </p>
            <p>
              ✓ <strong>Easy setup:</strong> Get started in just 2 minutes
            </p>
          </div>
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Results vary by industry and product. We can't guarantee specific outcomes, but we make the process as easy as possible.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OutputGallery; 